import { EmissionsModelReleaseLifecyclePhase } from '@watershed/constants/emissionsModel';
import { getBestLifecyclePhase } from '../../emRelease/LifecyclePhase';

/**
 * Represents a family version (calculation model) with its schema and lifecycle phases.
 */
type FamilyVersionForLifecyclePhase = {
  /** The ID of the schema this family version uses (from customizationSchema.id) */
  customizationSchemaId: string | null;
  /** The lifecycle phases of calculation methods that use this family version */
  lifecyclePhases: Array<EmissionsModelReleaseLifecyclePhase>;
};

/**
 * Returns the best lifecycle phase for a customization relative to a calculation
 * model's method family.
 *
 * A customization is compatible with a family version if they share the same
 * customization schema. The function returns the "best" lifecycle phase among
 * all compatible family versions, where "best" follows this priority:
 * Current > Beta > Alpha > LTS > Maintenance > EndOfLife
 *
 * @param customization - The customization with its schema ID
 * @param familyVersions - All versions in the model's method family, each with
 *                         their schema ID and lifecycle phases from associated
 *                         calculation methods
 * @returns The best lifecycle phase among compatible versions, or 'EndOfLife' if
 *          the customization is not compatible with any family version
 *
 * @example
 * ```ts
 * const phase = getCustomizationLifecyclePhaseForModel(
 *   { customizationSchemaId: 'schema-123' },
 *   [
 *     { customizationSchemaId: 'schema-123', lifecyclePhases: ['Beta'] },
 *     { customizationSchemaId: 'schema-123', lifecyclePhases: ['Current'] },
 *     { customizationSchemaId: 'schema-456', lifecyclePhases: ['Alpha'] },
 *   ]
 * );
 * // Returns 'Current' (best phase among compatible versions)
 * ```
 */
export function getCustomizationLifecyclePhaseForModel(
  { customizationSchemaId }: { customizationSchemaId: string },
  familyVersions: Array<FamilyVersionForLifecyclePhase>
): EmissionsModelReleaseLifecyclePhase {
  // Collect all lifecycle phases from compatible family versions
  const compatiblePhases: Array<EmissionsModelReleaseLifecyclePhase> = [];

  for (const version of familyVersions) {
    // A family version is compatible if it has the same schema as the customization
    if (version.customizationSchemaId === customizationSchemaId) {
      compatiblePhases.push(...version.lifecyclePhases);
    }
  }

  return getBestLifecyclePhase(compatiblePhases);
}
